/**
 * Sensor summary card — Vantage sensor inventory and current readings on the
 * site dashboard.
 *
 * Reads sensors directly from the unit entity embedded in the site dashboard
 * snapshot (unit.sensors[]) and renders every configured or reported row.
 * Sensors without a displayable temperature or humidity value remain visible
 * as compact name-only entries. The parent renders the card for every Vantage
 * unit, including the empty-report state. No Redux reads; the parent passes the
 * sensors array as a prop.
 *
 * Contains:
 * - SensorSummaryCard — relay-grid sensor data card (default export)
 */
import SensorsOutlinedIcon from '@mui/icons-material/SensorsOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import { Box, Card, IconButton, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useUser } from '@sophocles/app';
import { getUserMeasurementSystem } from '@sophocles/utils/formatUtils';
import { resolveSensorReadingDisplay } from '@sophocles/utils/sensorConversions';
import { getSensorTypeName } from '@sophocles/utils/sensorTypes';
import DashboardCardSkeleton from '@sophocles/views/sites/components/SiteDashboard/DashboardCardSkeleton';
import { getDisplayableSensorReadingTypes } from '@sophocles/views/sites/components/SiteDashboard/dashboardDerivations';

/**
 * Renders one labeled temperature or humidity value.
 */
function SensorReading({ label, reading, readingType, measurementSystem }) {
  const { value: displayValue, unit: displayUnit } = resolveSensorReadingDisplay(
    reading,
    readingType,
    measurementSystem,
  );

  if (displayValue == null) return null;

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 600 }}>
        {displayValue}
        {displayUnit}
      </Typography>
    </Box>
  );
}

SensorReading.propTypes = {
  label: PropTypes.string.isRequired,
  reading: PropTypes.shape({
    value: PropTypes.number,
    raw: PropTypes.number,
    unit: PropTypes.string,
  }),
  readingType: PropTypes.oneOf(['temperature', 'humidity']).isRequired,
  measurementSystem: PropTypes.oneOf(['imperial', 'metric']).isRequired,
};

/**
 * Renders one sensor as a detailed reading row or a compact name-only row.
 *
 * @param {object} props
 * @param {object} props.sensor - sensor entry from unit.sensors[]
 */
function SensorRow({ sensor, measurementSystem }) {
  const { t } = useTranslation();
  const readingTypes = getDisplayableSensorReadingTypes(sensor);
  const hasReading = readingTypes.length > 0;
  const sensorName =
    sensor.name ||
    (sensor.port_number != null
      ? t('sites.sensorPort', 'Port {{n}}', { n: sensor.port_number })
      : t('sensors.sensor', 'Sensor'));
  const typeLabel = sensor.sensor_type != null ? getSensorTypeName(sensor.sensor_type, t) : '';

  return (
    <Box
      component="li"
      sx={{
        px: 'var(--space-4)',
        py: hasReading ? 'var(--space-3)' : 'var(--space-2)',
        display: 'grid',
        gridTemplateColumns: hasReading
          ? {
              xs: `repeat(${Math.min(readingTypes.length, 2)}, minmax(0, 1fr))`,
              sm: `minmax(0, 1fr) repeat(${readingTypes.length}, minmax(6rem, 0.35fr))${typeLabel ? ' minmax(0, auto)' : ''}`,
            }
          : typeLabel
            ? { xs: 'minmax(0, 1fr)', sm: 'minmax(0, 1fr) minmax(0, auto)' }
            : 'minmax(0, 1fr)',
        columnGap: 'var(--space-4)',
        rowGap: 'var(--space-1)',
        alignItems: 'center',
        borderTop: 1,
        borderColor: 'divider',
        '&:first-of-type': { borderTop: 0 },
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          justifyContent: 'space-between',
          gap: 1,
          minWidth: 0,
          gridColumn: { xs: '1 / -1', sm: 'auto' },
        }}
      >
        <Typography
          variant={hasReading ? 'caption' : 'body2'}
          sx={{
            color: hasReading ? 'text.secondary' : 'text.primary',
            fontWeight: hasReading ? 500 : 600,
            minWidth: 0,
          }}
          noWrap
        >
          {sensorName}
        </Typography>

        {typeLabel ? (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', flexShrink: 0, whiteSpace: 'nowrap', display: { xs: 'block', sm: 'none' } }}
          >
            {typeLabel}
          </Typography>
        ) : null}
      </Box>

      {readingTypes.map(readingType => {
        const label = readingType === 'temperature' ? t('sites.temperature', 'Temp') : t('sites.humidity', 'Humidity');

        return (
          <SensorReading
            key={readingType}
            label={label}
            reading={sensor[readingType]}
            readingType={readingType}
            measurementSystem={measurementSystem}
          />
        );
      })}

      {typeLabel ? (
        <Typography
          variant="caption"
          sx={{
            color: 'text.secondary',
            textAlign: 'right',
            whiteSpace: 'nowrap',
            display: { xs: 'none', sm: 'block' },
          }}
        >
          {typeLabel}
        </Typography>
      ) : null}
    </Box>
  );
}

SensorRow.propTypes = {
  sensor: PropTypes.shape({
    key: PropTypes.string,
    display_order: PropTypes.number,
    sensor_type: PropTypes.number,
    port_number: PropTypes.number,
    name: PropTypes.string,
    has_reading: PropTypes.bool,
    temperature: PropTypes.shape({
      value: PropTypes.number,
      raw: PropTypes.number,
      unit: PropTypes.string,
    }),
    humidity: PropTypes.shape({
      value: PropTypes.number,
      raw: PropTypes.number,
      unit: PropTypes.string,
    }),
  }).isRequired,
  measurementSystem: PropTypes.oneOf(['imperial', 'metric']).isRequired,
};

function getSensorRowKey(sensor, index) {
  const identity = sensor.key ?? `${sensor.sensor_type ?? 'sensor'}:${sensor.port_number ?? index}`;
  return `${identity}:${sensor.display_order ?? index}`;
}

/**
 * @param {object}   props
 * @param {object[]} props.sensors        - sensor entries from unit.sensors[]
 * @param {boolean}  [props.loading=false] - show skeleton while data is loading
 * @param {Function} [props.onOpenSensorConfig] - opens the normalized SensorBlade
 */
export default function SensorSummaryCard({ sensors, loading = false, onOpenSensorConfig }) {
  const { t } = useTranslation();
  const { user } = useUser();
  const measurementSystem = getUserMeasurementSystem(user);

  if (loading) {
    return <DashboardCardSkeleton variant="sensor" />;
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.5, flexShrink: 0 }}>
        <Box
          sx={{
            width: 32,
            height: 32,
            borderRadius: 'var(--card-icon-radius)',
            bgcolor: 'action.hover',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <SensorsOutlinedIcon fontSize="small" />
        </Box>

        <Typography variant="subtitle2" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }} noWrap>
          {t('sites.sensors', 'Sensors')}
        </Typography>

        {onOpenSensorConfig ? (
          <Tooltip title={t('sites.sensorSettings', 'Sensor settings')}>
            <IconButton
              size="small"
              aria-label={t('sites.sensorSettings', 'Sensor settings')}
              onClick={event => {
                event.stopPropagation();
                onOpenSensorConfig();
              }}
            >
              <SettingsOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        ) : null}
      </Box>

      <Box sx={{ flex: 1, overflow: 'auto', borderTop: 1, borderColor: 'divider' }}>
        <Box component="ul" aria-label={t('sites.sensors', 'Sensors')} sx={{ m: 0, p: 0, listStyle: 'none' }}>
          {sensors.map((sensor, index) => (
            <SensorRow key={getSensorRowKey(sensor, index)} sensor={sensor} measurementSystem={measurementSystem} />
          ))}
        </Box>
      </Box>
    </Card>
  );
}

SensorSummaryCard.propTypes = {
  sensors: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  onOpenSensorConfig: PropTypes.func,
};
